import {
    Result,
    Task,
    Worker,
    WorkerError
} from "@tenera/upload_workers";

import AttachmentContentDAO from "../../content/AttachmentContentDAO";
import AttachmentRecordDAO from "../../record/AttachmentRecordDAO";
import AttachmentUploadRequest from "../request/AttachmentUploadRequest";
import AttachmentUploadRequestRevokedError from "../errors/AttachmentUploadRequestRevokedError";
import { AttachmentUploadStatus } from "../status/AttachmentUploadStatus";
import AttachmentUploadStatusDAO from "../status/AttachmentUploadStatusDAO";
import AttachmentUploadStatusNotFoundError from "../errors/AttachmentUploadStatusNotFoundError";
import { QueryClient } from "react-query";
import { IMAGE_QUERY_KEY } from "../../../../components/dashboard/data/hooks/useImages";

export default class AttachmentUploadWorker implements Worker<Task<AttachmentUploadRequest, AttachmentUploadStatus>, void> {
    private readonly attachmentUploadRequestTask: Task<AttachmentUploadRequest, AttachmentUploadStatus>;
    private readonly attachmentContentDAO: AttachmentContentDAO;
    private readonly cachingAttachmentRecordDAO: AttachmentRecordDAO;
    private readonly validationAttachmentRecordDAO: AttachmentRecordDAO;
    private readonly attachmentUploadStatusDAO: AttachmentUploadStatusDAO;
    private readonly queryClient: QueryClient;

    constructor(
        attachmentUploadRequestTask: Task<AttachmentUploadRequest, AttachmentUploadStatus>,
        attachmentContentDAO: AttachmentContentDAO,
        cachingAttachmentRecordDAO: AttachmentRecordDAO,
        validationAttachmentRecordDAO: AttachmentRecordDAO,
        attachmentUploadStatusDAO: AttachmentUploadStatusDAO,
        queryClient: QueryClient
    ) {
        this.attachmentUploadRequestTask = attachmentUploadRequestTask;
        this.attachmentContentDAO = attachmentContentDAO;
        this.cachingAttachmentRecordDAO = cachingAttachmentRecordDAO;
        this.validationAttachmentRecordDAO = validationAttachmentRecordDAO;
        this.attachmentUploadStatusDAO = attachmentUploadStatusDAO;
        this.queryClient = queryClient;
    }

    public async run(): Promise<Result<Task<AttachmentUploadRequest, AttachmentUploadStatus>, void>> {
        if (this.attachmentUploadRequestTask.status === AttachmentUploadStatus.COMPLETE) {
            this.attachmentUploadStatusDAO.removeAttachmentUploadStatus(this.attachmentUploadRequestTask.request.record.id);
            return {
                output: undefined,
                task: this.attachmentUploadRequestTask
            } satisfies Result<Task<AttachmentUploadRequest, AttachmentUploadStatus>, void>;
        }
        if (this.attachmentUploadRequestTask.status === AttachmentUploadStatus.PENDING_UPLOAD ||
            this.attachmentUploadRequestTask.status === AttachmentUploadStatus.UPLOAD_IN_PROGRESS ||
            this.attachmentUploadRequestTask.status === AttachmentUploadStatus.UPLOAD_FAILED
        ) {
            await this.upload();
        }
        if (this.attachmentUploadRequestTask.status === AttachmentUploadStatus.PENDING_RECORD_VALIDATION) {
            await this.validateAttachmentRecordExistsInAuthoritativeStore();
        }
        this.attachmentUploadStatusDAO.removeAttachmentUploadStatus(this.attachmentUploadRequestTask.request.record.id);
        return {
            output: undefined,
            task: this.attachmentUploadRequestTask
        } satisfies Result<Task<AttachmentUploadRequest, AttachmentUploadStatus>, void>;
    }

    private async upload() {
        try {
            // Instruct DataStore to attempt sync of the record to cloud, in case automatic sync failed
            this.cachingAttachmentRecordDAO.getById(this.attachmentUploadRequestTask.request.record.id);

            await this.updateUploadStatus(AttachmentUploadStatus.UPLOAD_IN_PROGRESS);
            await this.attachmentContentDAO.saveAttachment(
                this.attachmentUploadRequestTask.request.record.key!,
                this.attachmentUploadRequestTask.request.content
            );
            await this.updateUploadStatus(AttachmentUploadStatus.PENDING_RECORD_VALIDATION);
        } catch (error) {
            if (error instanceof AttachmentUploadRequestRevokedError) {
                return;
            }
            await this.updateUploadStatus(AttachmentUploadStatus.UPLOAD_FAILED);
            throw new WorkerError(this.attachmentUploadRequestTask.request.record.id, "Failed to Upload Attachment");
        }
    }

    private async validateAttachmentRecordExistsInAuthoritativeStore() {
        try {
            await this.validationAttachmentRecordDAO.getById(this.attachmentUploadRequestTask.request.record.id);
            await this.queryClient.invalidateQueries([IMAGE_QUERY_KEY, this.attachmentUploadRequestTask.request.record.parentId]);
            await this.queryClient.invalidateQueries([IMAGE_QUERY_KEY, 'property', this.attachmentUploadRequestTask.request.record.propertyId]);
            await this.updateUploadStatus(AttachmentUploadStatus.COMPLETE);
        } catch (error) {
            if (error instanceof AttachmentUploadRequestRevokedError) {
                return;
            }
            throw new WorkerError(this.attachmentUploadRequestTask.request.record.id, "Could not validate that attachment record was created");
        }
    }

    private async updateUploadStatus(
        status: AttachmentUploadStatus
    ): Promise<void> {
        try {
            await this.attachmentUploadStatusDAO.setAttachmentUploadStatus(
                this.attachmentUploadRequestTask.request.record.id,
                status
            );
            this.attachmentUploadRequestTask.status = status;
        } catch (error) {
            if (error instanceof AttachmentUploadStatusNotFoundError) {
                throw new AttachmentUploadRequestRevokedError(`Attachment upload request revoked for id: ${this.attachmentUploadRequestTask.request.record.id}`);
            }
            throw error;
        }
    }
}
